import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatRON } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  StickyNote, Coins, Landmark, Plus, Trash2, Pencil, Save, X,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "note", label: "Notite generale", icon: StickyNote, accent: "amber" },
  { key: "receivable", label: "De incasat", icon: Coins, accent: "emerald" },
  { key: "payment", label: "Credite / Plati", icon: Landmark, accent: "blue" },
];

function ymd(d) {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

// Days until ISO date (YYYY-MM-DD); negative = passed.
function daysUntil(iso) {
  if (!iso) return null;
  const today = new Date(ymd(new Date()) + "T00:00:00");
  const target = new Date(iso + "T00:00:00");
  if (isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function DaysBadge({ days }) {
  if (days === null || days === undefined) return null;
  let cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
  let label = `${days} zile`;
  if (days < 0) {
    cls = "bg-red-50 text-red-800 border-red-300";
    label = `RESTANT (${Math.abs(days)} zile)`;
  } else if (days === 0) {
    cls = "bg-red-50 text-red-800 border-red-300";
    label = "Scadent AZI";
  } else if (days <= 3) {
    cls = "bg-red-50 text-red-800 border-red-200";
  } else if (days <= 14) {
    cls = "bg-amber-50 text-amber-800 border-amber-200";
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider border ${cls}`}
      data-testid="note-days-left"
    >
      {label}
    </span>
  );
}

const emptyForm = (type) => ({
  id: null,
  type,
  title: "",
  content: "",
  person: "",
  bank: "",
  amount: "",
  due_date: "",
  installments: [],  // payment type only: [{ id, date, amount, paid }]
});

const newInstallmentId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
  `r-${Date.now()}-${Math.random()}`;

function saveButtonLabel(saving, editingId, addLabel) {
  if (saving) return "Se salveaza...";
  if (editingId) return "Salveaza modificarile";
  return addLabel;
}

export default function NotesPage() {
  const [allNotes, setAllNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("note");
  const [form, setForm] = useState(emptyForm("note"));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/notes");
      setAllNotes(data);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live refresh every 5s while tab is visible.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const handle = setInterval(tick, 5000);
    return () => clearInterval(handle);
  }, [load]);

  // Compute helper values for a payment note from its installments.
  //   - nextInstallment: first unpaid installment with the earliest date (today's
  //     installment counts as next due).
  //   - remainingTotal: sum of unpaid installment amounts.
  const paymentSummary = (n) => {
    const list = (n.installments || []).slice();
    let remaining = 0;
    let nextIns = null;
    for (const it of list) {
      if (it.paid) continue;
      remaining += Number(it.amount || 0);
      if (!nextIns || (it.date && it.date < (nextIns.date || "9999-12-31"))) {
        nextIns = it;
      }
    }
    return { nextIns, remaining };
  };

  const tabNotes = useMemo(
    () => allNotes.filter((n) => n.type === activeTab),
    [allNotes, activeTab]
  );

  // Aggregate totals (only shown for receivable + payment tabs).
  const tabTotal = useMemo(() => {
    if (activeTab === "payment") {
      // Sum only UNPAID installments across all credit entries.
      return tabNotes.reduce((acc, n) => acc + paymentSummary(n).remaining, 0);
    }
    return tabNotes.reduce((acc, n) => acc + Number(n.amount || 0), 0);
  }, [tabNotes, activeTab]);

  const switchTab = (key) => {
    setActiveTab(key);
    setForm(emptyForm(key));
  };

  const startEdit = (n) => setForm({
    id: n.id,
    type: n.type,
    title: n.title || "",
    content: n.content || "",
    person: n.person || "",
    bank: n.bank || "",
    amount: n.amount != null ? String(n.amount) : "",
    due_date: n.due_date || "",
    installments: (n.installments || []).map((it) => ({
      id: it.id || newInstallmentId(),
      date: it.date || "",
      amount: it.amount != null ? String(it.amount) : "",
      paid: !!it.paid,
    })),
  });

  const save = async () => {
    if (activeTab === "note" && !form.title.trim() && !form.content.trim()) {
      toast.error("Adauga un titlu sau text pentru notita");
      return;
    }
    if (activeTab === "receivable" && !form.person.trim()) {
      toast.error("Adauga numele persoanei de la care ai de incasat");
      return;
    }
    if (activeTab === "payment" && !form.bank.trim()) {
      toast.error("Adauga numele bancii / institutiei");
      return;
    }
    // Validate installments for payment
    if (activeTab === "payment") {
      for (const it of form.installments) {
        if (!it.date) {
          toast.error("Adauga data pentru fiecare rata");
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        title: form.title.trim(),
        content: form.content.trim(),
        person: form.person.trim(),
        bank: form.bank.trim(),
        amount: form.amount ? Number(form.amount) : 0,
        due_date: form.due_date || "",
      };
      if (form.type === "payment") {
        payload.installments = (form.installments || []).map((it) => ({
          id: it.id,
          date: it.date,
          amount: Number(it.amount || 0),
          paid: !!it.paid,
        }));
      }
      if (form.id) {
        await api.put(`/notes/${form.id}`, payload);
        toast.success("Notita actualizata");
      } else {
        await api.post("/notes", payload);
        toast.success("Notita adaugata");
      }
      setForm(emptyForm(activeTab));
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/notes/${confirmDelete.id}`);
      toast.success("Sters");
      if (form.id === confirmDelete.id) setForm(emptyForm(activeTab));
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la stergere");
    } finally {
      setConfirmDelete(null);
    }
  };

  // Toggle "paid" on a single installment without leaving the list view.
  // Sends the full installments list back to the backend (idempotent).
  const togglePaid = async (note, instId) => {
    try {
      const updated = (note.installments || []).map((it) =>
        it.id === instId ? { ...it, paid: !it.paid } : it
      );
      await api.put(`/notes/${note.id}`, {
        type: note.type,
        title: note.title || "",
        content: note.content || "",
        bank: note.bank || "",
        installments: updated,
      });
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  const activeTabConfig = TABS.find((t) => t.key === activeTab);
  const ActiveIcon = activeTabConfig.icon;

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="notes-page">
      <div className="mb-6">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
          Personal
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-black">
          Notite
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Notite generale, sume de incasat de la persoane si plati recurente (credite ipotecare etc.)
          cu calcul automat zile pana la scadenta.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-5 border-b border-zinc-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          const count = allNotes.filter((n) => n.type === t.key).length;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => switchTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 font-mono uppercase text-[11px] tracking-wider border-b-2 transition ${
                active
                  ? "border-black text-black"
                  : "border-transparent text-zinc-500 hover:text-black"
              }`}
              data-testid={`notes-tab-${t.key}`}
            >
              <Icon size={14} />
              {t.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-sm text-[9px] ${active ? "bg-black text-white" : "bg-zinc-100 text-zinc-600"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 items-start">
        {/* List */}
        <div className="border border-zinc-200 rounded-sm bg-white">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
            <h2 className="font-display text-xl font-bold tracking-tight text-black">
              {activeTabConfig.label}
            </h2>
            {(activeTab === "receivable" || activeTab === "payment") && tabNotes.length > 0 && (
              <div className="flex flex-col items-end">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {activeTab === "receivable" ? "Total de incasat" : "Total ramas (neplatit)"}
                </span>
                <span
                  className="font-display text-xl font-black tracking-tighter text-black"
                  data-testid={`notes-total-${activeTab}`}
                >
                  {formatRON(tabTotal)}
                </span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-10 text-center text-zinc-500 font-mono text-sm">Se incarca...</div>
          ) : tabNotes.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">
              <ActiveIcon size={32} className="mx-auto mb-2 text-zinc-300" />
              <div className="font-mono text-sm">
                Nicio inregistrare. Adauga prima folosind formularul din dreapta.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {tabNotes.map((n) => (
                <div key={n.id} className="p-4" data-testid={`note-row-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {activeTab === "note" && (
                        <>
                          {n.title && (
                            <div className="font-display text-base font-bold tracking-tight text-black mb-1">
                              {n.title}
                            </div>
                          )}
                          {n.content && (
                            <div className="text-sm text-zinc-700 whitespace-pre-wrap" data-testid={`note-content-${n.id}`}>
                              {n.content}
                            </div>
                          )}
                        </>
                      )}
                      {activeTab === "receivable" && (
                        <>
                          <div className="flex items-baseline gap-3 flex-wrap mb-1">
                            <span className="font-display text-base font-bold tracking-tight text-black" data-testid={`note-person-${n.id}`}>
                              {n.person}
                            </span>
                            <span className="font-display text-lg font-black text-emerald-700 font-mono" data-testid={`note-amount-${n.id}`}>
                              {formatRON(Number(n.amount || 0))}
                            </span>
                            {n.due_date && <DaysBadge days={daysUntil(n.due_date)} />}
                          </div>
                          {n.due_date && (
                            <div className="font-mono text-[11px] text-zinc-500">
                              Scadent: {n.due_date}
                            </div>
                          )}
                          {n.content && (
                            <div className="text-sm text-zinc-600 mt-1 whitespace-pre-wrap">{n.content}</div>
                          )}
                        </>
                      )}
                      {activeTab === "payment" && (() => {
                        const { nextIns, remaining } = paymentSummary(n);
                        const insts = (n.installments || []);
                        return (
                          <>
                            <div className="flex items-baseline gap-3 flex-wrap mb-2">
                              <span className="font-display text-base font-bold tracking-tight text-black" data-testid={`note-bank-${n.id}`}>
                                {n.bank}
                              </span>
                              {n.title && (
                                <span className="font-mono text-xs text-zinc-600">
                                  · {n.title}
                                </span>
                              )}
                              <span className="font-display text-lg font-black text-blue-700 font-mono ml-auto" data-testid={`note-amount-${n.id}`}>
                                {formatRON(remaining)}
                              </span>
                            </div>
                            {nextIns && (
                              <div className="flex items-center gap-2 mb-2 font-mono text-[11px]">
                                <span className="text-zinc-500 uppercase tracking-widest">Urmatoarea rata:</span>
                                <span className="text-black">{nextIns.date}</span>
                                <span className="text-black">— {formatRON(Number(nextIns.amount || 0))}</span>
                                <DaysBadge days={daysUntil(nextIns.date)} />
                              </div>
                            )}
                            {insts.length > 0 ? (
                              <div className="border border-zinc-200 rounded-sm overflow-hidden mt-2">
                                <table className="w-full text-sm">
                                  <thead className="bg-zinc-50">
                                    <tr>
                                      <th className="text-left px-2 py-1.5 font-semibold uppercase text-[10px] tracking-wide text-black">Data</th>
                                      <th className="text-right px-2 py-1.5 font-semibold uppercase text-[10px] tracking-wide text-black">Suma</th>
                                      <th className="text-center px-2 py-1.5 font-semibold uppercase text-[10px] tracking-wide text-black">Zile</th>
                                      <th className="text-center px-2 py-1.5 font-semibold uppercase text-[10px] tracking-wide text-black w-16">Platit</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {insts.map((it) => (
                                      <tr
                                        key={it.id}
                                        className={`border-t border-zinc-100 ${it.paid ? "bg-emerald-50/30 text-zinc-400 line-through" : ""}`}
                                        data-testid={`note-inst-row-${it.id}`}
                                      >
                                        <td className="px-2 py-1.5 font-mono">{it.date}</td>
                                        <td className="px-2 py-1.5 font-mono text-right">{formatRON(Number(it.amount || 0))}</td>
                                        <td className="px-2 py-1.5 text-center">
                                          {!it.paid && it.date && <DaysBadge days={daysUntil(it.date)} />}
                                        </td>
                                        <td className="px-2 py-1.5 text-center">
                                          <input
                                            type="checkbox"
                                            checked={!!it.paid}
                                            onChange={() => togglePaid(n, it.id)}
                                            className="h-4 w-4 accent-emerald-600 cursor-pointer"
                                            data-testid={`note-inst-paid-${it.id}`}
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-xs text-zinc-500 font-mono italic mt-1">
                                Fara rate inregistrate.
                              </div>
                            )}
                            {n.content && (
                              <div className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap">{n.content}</div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEdit(n)}
                        className="h-8 w-8 hover:bg-blue-50"
                        data-testid={`edit-note-${n.id}`}
                        title="Editeaza"
                      >
                        <Pencil size={14} className="text-blue-700" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmDelete(n)}
                        className="h-8 w-8 hover:bg-red-50"
                        data-testid={`delete-note-${n.id}`}
                        title="Sterge"
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form (create / edit) */}
        <div className="border border-zinc-200 rounded-sm bg-white p-5 lg:sticky lg:top-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold tracking-tight text-black">
              {form.id ? "Editeaza" : "Adauga"}
            </h2>
            {form.id && (
              <button
                type="button"
                onClick={() => setForm(emptyForm(activeTab))}
                className="font-mono uppercase text-[10px] tracking-wider text-zinc-500 hover:text-black flex items-center gap-1"
                data-testid="note-form-cancel"
              >
                <X size={11} /> Anuleaza edit
              </button>
            )}
          </div>

          {/* Type-specific fields */}
          {form.type === "note" && (
            <>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Titlu</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Subiectul notitei (optional)"
                className="mt-1 mb-3 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="note-title-input"
              />
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Continut</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Scrie nota aici..."
                rows={8}
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="note-content-input"
              />
            </>
          )}

          {form.type === "receivable" && (
            <>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">De la</Label>
              <Input
                value={form.person}
                onChange={(e) => setForm({ ...form, person: e.target.value })}
                placeholder="Numele persoanei"
                className="mt-1 mb-3 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="note-person-input"
              />
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Suma (RON)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black font-mono"
                    data-testid="note-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Scadent la</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                    data-testid="note-due-input"
                  />
                </div>
              </div>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Detalii (optional)</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Pentru ce, observatii..."
                rows={4}
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="note-content-input"
              />
            </>
          )}

          {form.type === "payment" && (
            <>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Banca / Institutie</Label>
              <Input
                value={form.bank}
                onChange={(e) => setForm({ ...form, bank: e.target.value })}
                placeholder="BCR, ING, Raiffeisen..."
                className="mt-1 mb-3 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="note-bank-input"
              />
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Tip credit (optional)</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Credit ipotecar, Leasing auto..."
                className="mt-1 mb-3 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="note-title-input"
              />

              {/* Installments editor — table of (data, suma, platit) rows */}
              <div className="pt-3 border-t border-dashed border-zinc-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                    Rate (data + suma)
                  </Label>
                  <button
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      installments: [...form.installments, { id: newInstallmentId(), date: "", amount: "", paid: false }],
                    })}
                    className="px-2 py-1 rounded-sm border border-zinc-300 bg-white text-black hover:border-zinc-500 font-mono uppercase text-[10px] tracking-wider"
                    data-testid="note-installment-add"
                  >
                    + Adauga rata
                  </button>
                </div>
                {form.installments.length === 0 ? (
                  <div className="text-[11px] text-zinc-500 font-mono italic">
                    Adauga rate cu data si suma. Sistemul calculeaza automat urmatoarea rata si totalul ramas.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {form.installments.map((it, idx) => (
                      <div key={it.id} className="flex items-center gap-1.5" data-testid={`note-installment-row-${idx}`}>
                        <Input
                          type="date"
                          value={it.date}
                          onChange={(e) => {
                            const next = [...form.installments];
                            next[idx] = { ...next[idx], date: e.target.value };
                            setForm({ ...form, installments: next });
                          }}
                          className="h-9 rounded-sm border-zinc-300 focus-visible:ring-black text-xs flex-1"
                          data-testid={`note-installment-date-${idx}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.amount}
                          onChange={(e) => {
                            const next = [...form.installments];
                            next[idx] = { ...next[idx], amount: e.target.value };
                            setForm({ ...form, installments: next });
                          }}
                          placeholder="rata"
                          className="h-9 rounded-sm border-zinc-300 focus-visible:ring-black text-xs w-24 font-mono"
                          data-testid={`note-installment-amount-${idx}`}
                        />
                        <label className="flex items-center gap-1 px-1.5 py-1 cursor-pointer" title="Platit">
                          <input
                            type="checkbox"
                            checked={!!it.paid}
                            onChange={(e) => {
                              const next = [...form.installments];
                              next[idx] = { ...next[idx], paid: e.target.checked };
                              setForm({ ...form, installments: next });
                            }}
                            className="h-4 w-4 accent-emerald-600"
                            data-testid={`note-installment-paid-${idx}`}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const next = form.installments.filter((_, i) => i !== idx);
                            setForm({ ...form, installments: next });
                          }}
                          className="text-red-500 hover:text-red-700 p-1.5"
                          data-testid={`note-installment-remove-${idx}`}
                          title="Sterge rata"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Label className="mt-3 block text-black font-semibold uppercase text-[10px] tracking-wide">Detalii (optional)</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Numar cont, observatii..."
                rows={3}
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="note-content-input"
              />
            </>
          )}

          <Button
            onClick={save}
            disabled={saving}
            className="w-full mt-5 rounded-sm bg-black hover:bg-zinc-800 text-white h-11"
            data-testid="note-save"
          >
            <Save size={16} className="mr-2" />
            {saveButtonLabel(saving, form.id, "Adauga")}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sterge inregistrarea?</AlertDialogTitle>
            <AlertDialogDescription>
              Aceasta actiune nu poate fi anulata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuleaza</AlertDialogCancel>
            <AlertDialogAction onClick={removeNote} className="bg-red-600 hover:bg-red-700">
              Sterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
